<!-- Desktop: p-dialog right-side panel -->
@if (isDesktopView()) {
<p-dialog
  [(visible)]="visible"
  [draggable]="false"
  [resizable]="false"
  [modal]="true"
  [closable]="true"
  [header]="'profile.title' | transloco"
  [appendTo]="'body'"
  [styleClass]="'profile-dialog'"
  maskStyleClass="profile-dialog-mask"
  (onHide)="onDialogHide()"
  >
  <ng-template pTemplate="header">
    <div class="profile-header">
      <div class="profile-header__avatar-wrapper">
        <app-avatar
          [userId]="userId"
          [name]="user ? user.firstName + ' ' + (user.lastName || '') : ''"
          [size]="88"
          [showInfo]="false"
          [showTooltip]="false"
          [enablePreview]="false"
        ></app-avatar>
      </div>

      <h2 class="profile-header__name">
        @if (user) {
          {{ user.firstName }} {{ user.lastName }}
        } @else {
          {{ 'common.labels.loading' | transloco }}
        }
      </h2>

      @if (user?.roles?.length && !isClient) {
        <span class="profile-header__role-badge">{{ user?.roles?.[0] }}</span>
      }

      @if (user?.createdAt) {
        <p class="profile-header__member-since">
          <lucide-icon name="Calendar" [size]="14"></lucide-icon>
          {{ 'profile.memberSince' | transloco }} {{ formatMemberSince(user?.createdAt) }}
        </p>
      }
    </div>
  </ng-template>

  <form [formGroup]="profileForm" (ngSubmit)="onSave()" class="profile-form">
    @if (isLoading) {
      <div class="profile-content">
        <div class="profile-skeleton">
          <p-skeleton width="100%" height="80px" borderRadius="16px"></p-skeleton>
          <p-skeleton width="100%" height="120px" borderRadius="16px"></p-skeleton>
          <p-skeleton width="100%" height="100px" borderRadius="16px"></p-skeleton>
        </div>
      </div>
    } @else {
      <div class="profile-content">

        <!-- Section: Personal Info -->
        <section class="profile-section">
          <div class="profile-section__header">
            <h3 class="profile-section__title">
              <lucide-icon name="User" [size]="16"></lucide-icon>
              {{ 'profile.sections.personalInfo' | transloco }}
            </h3>
            @if (!isEditMode) {
              <button type="button" class="profile-section__edit-btn" (click)="enableEditMode()" [disabled]="showPasswordFields" [attr.aria-label]="'profile.edit.actions.editPersonalInfo' | transloco">
                <lucide-icon name="Pencil" [size]="16"></lucide-icon>
              </button>
            }
          </div>

          <div class="profile-card">
            @if (!isEditMode) {
              <div class="profile-field">
                <div class="profile-field__label">
                  <lucide-icon name="User" [size]="16"></lucide-icon>
                  <span>{{ 'profile.fullName' | transloco }}</span>
                </div>
                <span class="profile-field__value">
                  {{ user?.firstName }} {{ user?.lastName }}{{ user?.secondLastName ? ' ' + user?.secondLastName : '' }}
                </span>
              </div>
            } @else {
              <div class="profile-card__edit-fields">
                <app-input
                  [label]="'profile.edit.fields.firstName' | transloco"
                  [placeholder]="'profile.edit.placeholders.firstName' | transloco"
                  formControlName="firstName" [required]="true" [error]="hasError('firstName')" [errorMessage]="fieldError('firstName')"
                  type="text"
                  size="small"
                ></app-input>
                <div class="profile-card__row-two">
                  <app-input
                    [label]="'profile.edit.fields.lastName' | transloco"
                    [placeholder]="'profile.edit.placeholders.lastName' | transloco"
                    formControlName="lastName" [required]="true" [error]="hasError('lastName')" [errorMessage]="fieldError('lastName')"
                    type="text"
                    size="small"
                  ></app-input>
                  <app-input
                    [label]="'profile.edit.fields.secondLastName' | transloco"
                    [placeholder]="'profile.edit.placeholders.secondLastName' | transloco"
                    formControlName="secondLastName"
                    type="text"
                    size="small"
                  ></app-input>
                </div>
              </div>
            }
          </div>
        </section>

        <!-- Section: Contact -->
        <section class="profile-section">
          <div class="profile-section__header">
            <h3 class="profile-section__title">
              <lucide-icon name="Mail" [size]="16"></lucide-icon>
              {{ 'profile.sections.contact' | transloco }}
            </h3>
          </div>

          <div class="profile-card">
            <div class="profile-field">
              <div class="profile-field__label">
                <lucide-icon name="Mail" [size]="16"></lucide-icon>
                <span>{{ 'profile.edit.fields.email' | transloco }}</span>
              </div>
              @if (isEditMode) {
                <div class="profile-field__input">
                  <app-input formControlName="email" [required]="true" [error]="hasError('email')" [errorMessage]="fieldError('email')" type="email" size="small"></app-input>
                </div>
                @if (emailChanged) {
                  <div class="profile-field__input">
                    <app-input formControlName="currentPassword" type="password" size="small" [required]="true" [placeholder]="'profile.edit.fields.currentPassword' | transloco" [helperText]="'profile.edit.fields.currentPasswordHelp' | transloco" [error]="hasError('currentPassword')" [errorMessage]="fieldError('currentPassword')"></app-input>
                  </div>
                }
              } @else {
                <span class="profile-field__value">{{ profileForm.get('email')?.value || '-' }}</span>
              }
            </div>

            <div class="profile-card__divider"></div>

            <div class="profile-field">
              <div class="profile-field__label">
                <lucide-icon name="Phone" [size]="16"></lucide-icon>
                <span>{{ 'profile.edit.fields.phone1' | transloco }}</span>
              </div>
              @if (isEditMode) {
                <div class="profile-field__input">
                  <app-phone formControlName="phone" size="small"></app-phone>
                </div>
              } @else {
                <span class="profile-field__value">
                  @if (getPhoneFlagUrl(profileForm.get('phone')?.value)) {
                    <img [src]="getPhoneFlagUrl(profileForm.get('phone')?.value)!" [alt]="'common.a11y.flag' | transloco" class="profile-field__flag" />
                  }
                  {{ formatPhoneDisplay(profileForm.get('phone')?.value) }}
                </span>
              }
            </div>

            @if (showSecondPhone) {
              <div class="profile-card__divider"></div>
              <div class="profile-field">
                <div class="profile-field__label">
                  <lucide-icon name="Phone" [size]="16"></lucide-icon>
                  <span>{{ 'profile.edit.fields.phone2' | transloco }}</span>
                </div>
                @if (isEditMode) {
                  <div class="profile-field__input">
                    <app-phone formControlName="phone2" size="small"></app-phone>
                  </div>
                } @else {
                  <span class="profile-field__value">
                    @if (getPhoneFlagUrl(profileForm.get('phone2')?.value)) {
                      <img [src]="getPhoneFlagUrl(profileForm.get('phone2')?.value)!" [alt]="'common.a11y.flag' | transloco" class="profile-field__flag" />
                    }
                    {{ formatPhoneDisplay(profileForm.get('phone2')?.value) }}
                  </span>
                }
              </div>
            }

            @if (isEditMode && !showSecondPhone) {
              <button type="button" class="profile-card__add-link" (click)="addSecondPhone()">
                <lucide-icon name="Plus" [size]="14"></lucide-icon>
                {{ 'profile.edit.actions.addPhone' | transloco }}
              </button>
            } @else if (isEditMode && showSecondPhone) {
              <button type="button" class="profile-card__remove-link" (click)="removeSecondPhone()">
                <lucide-icon name="Minus" [size]="14"></lucide-icon>
                {{ 'profile.edit.actions.removePhone' | transloco }}
              </button>
            }
          </div>
        </section>

        <!-- Section: Security -->
        <section class="profile-section">
          <div class="profile-section__header">
            <h3 class="profile-section__title">
              <lucide-icon name="Shield" [size]="16"></lucide-icon>
              {{ 'profile.sections.security' | transloco }}
            </h3>
          </div>

          <div class="profile-card">
            <div class="profile-field">
              <div class="profile-field__label">
                <lucide-icon name="Lock" [size]="16"></lucide-icon>
                <span>{{ 'profile.edit.fields.password' | transloco }}</span>
              </div>
              <div class="profile-field__action">
                <span class="profile-field__dots">&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;</span>
                @if (!showPasswordFields) {
                  <button type="button" class="profile-field__change-btn" (click)="togglePasswordFields()" [disabled]="isEditMode">
                    {{ 'profile.edit.actions.changePassword' | transloco }}
                  </button>
                }
              </div>
            </div>

            @if (showPasswordFields) {
              <app-change-password variant="inline" (closeDialog)="cancelPasswordChange()"></app-change-password>
            }

            @if (user && !isClient) {
              <app-profile-mfa-section [disabled]="isEditMode"></app-profile-mfa-section>
            }
          </div>
        </section>
      </div>

      <!-- Actions -->
      <div class="profile-actions">
        @if (isEditMode) {
          <div class="profile-actions__edit-mode">
            <app-button
              [label]="'profile.edit.actions.cancel' | transloco"
              variant="ghost"
              type="button"
              size="small"
              (click)="cancelEdit()"
            ></app-button>
            <app-button
              [label]="'common.actions.saveChanges' | transloco"
              type="submit"
              size="small"
            ></app-button>
          </div>
        } @else {
          @if (isClient) {
            <button type="button" class="profile-actions__logout" (click)="logout()">
              <lucide-icon name="LogOut" [size]="18"></lucide-icon>
              {{ 'layout.profile.logout' | transloco }}
            </button>
          }
        }
      </div>
    }
  </form>
</p-dialog>
} @else if (visible) {
  <!-- Responsive: slide-up modal -->
  <div class="profile-overlay" animate.leave="bt-sheet-leave" (click)="onClose()">
    <div class="profile-modal bt-sheet" (click)="$event.stopPropagation()">
      <div class="profile-header profile-header--mobile">
        <button type="button" class="profile-header__close-btn" (click)="onClose()" [attr.aria-label]="'common.actions.close' | transloco">
          <lucide-icon name="X" [size]="24"></lucide-icon>
        </button>
        <div class="profile-header__avatar-wrapper">
          <app-avatar
            [userId]="userId"
            [name]="user ? user.firstName + ' ' + (user.lastName || '') : ''"
            [size]="72"
            [showInfo]="false"
            [showTooltip]="false"
            [enablePreview]="false"
          ></app-avatar>
        </div>
        <h2 class="profile-header__name">
          @if (user) {
            {{ user.firstName }} {{ user.lastName }}
          } @else {
            {{ 'common.labels.loading' | transloco }}
          }
        </h2>
        @if (user?.roles?.length && !isClient) {
          <span class="profile-header__role-badge">{{ user?.roles?.[0] }}</span>
        }
        @if (user?.createdAt) {
          <p class="profile-header__member-since">
            <lucide-icon name="Calendar" [size]="14"></lucide-icon>
            {{ 'profile.memberSince' | transloco }} {{ formatMemberSince(user?.createdAt) }}
          </p>
        }
      </div>
      <form [formGroup]="profileForm" (ngSubmit)="onSave()" class="profile-form profile-form--mobile">
        @if (isLoading) {
          <div class="profile-content">
            <div class="profile-skeleton">
              <p-skeleton width="100%" height="80px" borderRadius="12px"></p-skeleton>
              <p-skeleton width="100%" height="120px" borderRadius="12px"></p-skeleton>
              <p-skeleton width="100%" height="100px" borderRadius="12px"></p-skeleton>
            </div>
          </div>
        } @else {
          <div class="profile-content">
            <!-- Section: Personal Info -->
            <section class="profile-section">
              <div class="profile-section__header">
                <h3 class="profile-section__title">
                  <lucide-icon name="User" [size]="16"></lucide-icon>
                  {{ 'profile.sections.personalInfo' | transloco }}
                </h3>
                @if (!isEditMode) {
                  <button type="button" class="profile-section__edit-btn" (click)="enableEditMode()" [disabled]="showPasswordFields" [attr.aria-label]="'profile.edit.actions.editPersonalInfo' | transloco">
                    <lucide-icon name="Pencil" [size]="16"></lucide-icon>
                  </button>
                }
              </div>
              <div class="profile-card">
                @if (!isEditMode) {
                  <div class="profile-field">
                    <div class="profile-field__label">
                      <lucide-icon name="User" [size]="16"></lucide-icon>
                      <span>{{ 'profile.fullName' | transloco }}</span>
                    </div>
                    <span class="profile-field__value">
                      {{ user?.firstName }} {{ user?.lastName }}{{ user?.secondLastName ? ' ' + user?.secondLastName : '' }}
                    </span>
                  </div>
                } @else {
                  <div class="profile-card__edit-fields">
                    <app-input [label]="'profile.edit.fields.firstName' | transloco" formControlName="firstName" [required]="true" [error]="hasError('firstName')" [errorMessage]="fieldError('firstName')" size="small"></app-input>
                    <app-input [label]="'profile.edit.fields.lastName' | transloco" formControlName="lastName" [required]="true" [error]="hasError('lastName')" [errorMessage]="fieldError('lastName')" size="small"></app-input>
                    <app-input [label]="'profile.edit.fields.secondLastName' | transloco" formControlName="secondLastName" size="small"></app-input>
                  </div>
                }
              </div>
            </section>
            <!-- Section: Contact -->
            <section class="profile-section">
              <div class="profile-section__header">
                <h3 class="profile-section__title">
                  <lucide-icon name="Mail" [size]="16"></lucide-icon>
                  {{ 'profile.sections.contact' | transloco }}
                </h3>
              </div>
              <div class="profile-card">
                <div class="profile-field">
                  <div class="profile-field__label">
                    <lucide-icon name="Mail" [size]="16"></lucide-icon>
                    <span>{{ 'profile.edit.fields.email' | transloco }}</span>
                  </div>
                  @if (isEditMode) {
                    <div class="profile-field__input"><app-input formControlName="email" [required]="true" [error]="hasError('email')" [errorMessage]="fieldError('email')" type="email" size="small"></app-input></div>
                    @if (emailChanged) {
                      <div class="profile-field__input"><app-input formControlName="currentPassword" type="password" size="small" [required]="true" [placeholder]="'profile.edit.fields.currentPassword' | transloco" [helperText]="'profile.edit.fields.currentPasswordHelp' | transloco" [error]="hasError('currentPassword')" [errorMessage]="fieldError('currentPassword')"></app-input></div>
                    }
                  } @else {
                    <span class="profile-field__value">{{ profileForm.get('email')?.value || '-' }}</span>
                  }
                </div>
                <div class="profile-card__divider"></div>
                <div class="profile-field">
                  <div class="profile-field__label">
                    <lucide-icon name="Phone" [size]="16"></lucide-icon>
                    <span>{{ 'profile.edit.fields.phone1' | transloco }}</span>
                  </div>
                  @if (isEditMode) {
                    <div class="profile-field__input"><app-phone formControlName="phone" size="small"></app-phone></div>
                  } @else {
                    <span class="profile-field__value">
                      @if (getPhoneFlagUrl(profileForm.get('phone')?.value)) {
                        <img [src]="getPhoneFlagUrl(profileForm.get('phone')?.value)!" [alt]="'common.a11y.flag' | transloco" class="profile-field__flag" />
                      }
                      {{ formatPhoneDisplay(profileForm.get('phone')?.value) }}
                    </span>
                  }
                </div>
                @if (showSecondPhone) {
                  <div class="profile-card__divider"></div>
                  <div class="profile-field">
                    <div class="profile-field__label">
                      <lucide-icon name="Phone" [size]="16"></lucide-icon>
                      <span>{{ 'profile.edit.fields.phone2' | transloco }}</span>
                    </div>
                    @if (isEditMode) {
                      <div class="profile-field__input"><app-phone formControlName="phone2" size="small"></app-phone></div>
                    } @else {
                      <span class="profile-field__value">
                        @if (getPhoneFlagUrl(profileForm.get('phone2')?.value)) {
                          <img [src]="getPhoneFlagUrl(profileForm.get('phone2')?.value)!" [alt]="'common.a11y.flag' | transloco" class="profile-field__flag" />
                        }
                        {{ formatPhoneDisplay(profileForm.get('phone2')?.value) }}
                      </span>
                    }
                  </div>
                }
                @if (isEditMode && !showSecondPhone) {
                  <button type="button" class="profile-card__add-link" (click)="addSecondPhone()">
                    <lucide-icon name="Plus" [size]="14"></lucide-icon>
                    {{ 'profile.edit.actions.addPhone' | transloco }}
                  </button>
                } @else if (isEditMode && showSecondPhone) {
                  <button type="button" class="profile-card__remove-link" (click)="removeSecondPhone()">
                    <lucide-icon name="Minus" [size]="14"></lucide-icon>
                    {{ 'profile.edit.actions.removePhone' | transloco }}
                  </button>
                }
              </div>
            </section>
            <!-- Section: Security -->
            <section class="profile-section">
              <div class="profile-section__header">
                <h3 class="profile-section__title">
                  <lucide-icon name="Shield" [size]="16"></lucide-icon>
                  {{ 'profile.sections.security' | transloco }}
                </h3>
              </div>
              <div class="profile-card">
                <div class="profile-field">
                  <div class="profile-field__label">
                    <lucide-icon name="Lock" [size]="16"></lucide-icon>
                    <span>{{ 'profile.edit.fields.password' | transloco }}</span>
                  </div>
                  <div class="profile-field__action">
                    <span class="profile-field__dots">&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;&#x2022;</span>
                    @if (!showPasswordFields) {
                      <button type="button" class="profile-field__change-btn" (click)="changePassword()" [disabled]="isEditMode">
                        {{ 'profile.edit.actions.changePassword' | transloco }}
                      </button>
                    }
                  </div>
                </div>
                @if (user && !isClient) {
                  <app-profile-mfa-section [disabled]="isEditMode"></app-profile-mfa-section>
                }
              </div>
            </section>
          </div>
          <div class="profile-actions">
            @if (isEditMode) {
              <div class="profile-actions__edit-mode">
                <app-button [label]="'profile.edit.actions.cancel' | transloco" variant="ghost" type="button" size="small" (click)="cancelEdit()"></app-button>
                <app-button [label]="'common.actions.saveChanges' | transloco" type="submit" size="small"></app-button>
              </div>
            } @else {
              @if (isClient) {
                <button type="button" class="profile-actions__logout" (click)="logout()">
                  <lucide-icon name="LogOut" [size]="18"></lucide-icon>
                  {{ 'layout.profile.logout' | transloco }}
                </button>
              }
            }
          </div>
        }
      </form>
    </div>
  </div>
}

<!-- Password change dialog - Desktop (header hidden: the form has its own close button; closable keeps Escape working) -->
@if (isDesktopView()) {
<p-dialog
  [(visible)]="showPasswordDialog"
  [draggable]="false"
  [resizable]="false"
  [modal]="true"
  [closable]="true"
  [showHeader]="false"
  [appendTo]="'body'"
  [baseZIndex]="20000"
  styleClass="password-dialog"
  maskStyleClass="password-dialog-mask"
  (click)="$event.stopPropagation()"
  >
  <app-change-password (closeDialog)="onClosePasswordDialog()"></app-change-password>
</p-dialog>
} @else if (showPasswordDialog) {
  <!-- Password change dialog - Responsive -->
  <div class="password-overlay" animate.leave="bt-sheet-leave" (click)="onClosePasswordDialog()">
    <div class="password-modal bt-sheet" (click)="$event.stopPropagation()">
      <app-change-password (closeDialog)="onClosePasswordDialog()"></app-change-password>
    </div>
  </div>
}

<!-- MFA Enrollment Dialog -->
<app-mfa-enrollment-dialog
  [(visible)]="mfa.showDialog"
  (mfaStatusChanged)="mfa.onEnrollmentFinished()"
></app-mfa-enrollment-dialog>
